import wikitTailwindConf from '@wiseed/wikit/dist/tailwind.config';
import { colors } from '@app/theme/colors';
import type { MessageFormatter } from 'svelte-i18n';
import { capitalize, decamelize, frDateFnsLocale } from '@wiseed/wikit';
import type {
	FinancialDiversification,
	SectorialDiversification
} from '@app/models/api/PortfolioStatsModel';
import type {
	GetChartsSharedOptionsType,
	GetChartColorsType,
	CarbonChartTabularData
} from '@wiseed/wikit';
import type { RecursiveKeyValuePair, ResolvableTo } from 'tailwindcss/types/config';

const allColors: ResolvableTo<RecursiveKeyValuePair> = {
	...wikitTailwindConf.theme.colors,
	...colors
};

export const getChartColors: GetChartColorsType = (data) => {
	if (!data) {
		return {};
	}

	const chartColors: ResolvableTo<RecursiveKeyValuePair> = {};
	const groupColorsMap: ResolvableTo<RecursiveKeyValuePair> = {};
	let uncoloredGroupIndex = 0;

	data.forEach((entry) => {
		const groupName: string = entry.group;
		const groupColorKey: string = entry.color;

		if (groupColorKey) {
			const groupColorValue = allColors[groupColorKey]?.DEFAULT ?? allColors[groupColorKey];
			if (!groupColorsMap[groupName]) {
				groupColorsMap[groupName] = groupColorValue;
				chartColors[groupName] = groupColorValue;
			}
		} else {
			if (!groupColorsMap[groupName]) {
				const colorFromPalette = Object.entries(allColors.charts)[uncoloredGroupIndex++]?.[1];
				if (colorFromPalette) {
					groupColorsMap[groupName] = colorFromPalette;
					chartColors[groupName] = colorFromPalette;
				}
			}
		}
	});

	return chartColors;
};

export const getChartsSharedOptions: GetChartsSharedOptionsType = (
	data,
	title = '',
	height = '400px',
	gradientColor: boolean = false
) => {
	return {
		title: title,
		height: height,
		color: {
			scale: getChartColors(data),
			gradient: { enabled: gradientColor }
		},
		data: {
			loading: !data.length
		},
		timeScale: {
			localeObject: frDateFnsLocale
		},
		locale: {
			// config à surcharger pour le formatage des dates voir => https://github.com/carbon-design-system/carbon-charts/blob/e162c521dd29d418eea1b418300454ffe9dc6134/packages/core/src/configuration.ts
			code: 'fr-FR',
			optionsObject: {
				hourly: {
					primary: {
						'MMM d, hh a': {
							month: 'short',
							day: 'numeric',
							hour: '2-digit',
							hourCycle: 'h23'
						}
					},
					secondary: {
						'hh a': {
							hour: '2-digit',
							hourCycle: 'h23'
						}
					},
					type: 'time'
				}
			}
		}
	};
};

export const getDiversificationData = (
	diversification: FinancialDiversification | SectorialDiversification,
	$t: MessageFormatter,
	isFinancialDiversificationData: boolean = false
): CarbonChartTabularData => {
	const data: CarbonChartTabularData = [];
	const keys = Object.keys(diversification) as Array<keyof typeof diversification>;
	keys.forEach((key) => {
		if (diversification[key]) {
			data.push({
				group: isFinancialDiversificationData
					? capitalize($t(`financialInstrument.${key}`))
					: capitalize($t(`sector.${key}`)),
				value: diversification[key],
				color: decamelize(key, '-')
			});
		}
	});
	return data;
};
